/**
 * The reusable invite link's calls: `GET /public/advisor-public-profile/:advisorId` (see
 * useAdvisorPublicProfile) and `POST /public/client-invite` (see usePublicInviteFlow).
 *
 * They use plain fetch on purpose rather than WaterlilyClient. The endpoint is public, and the
 * visitor is usually signed out. WaterlilyClient would fetch a Clerk token before every request
 * (failing with a 503 when one cannot be had) and sign the visitor out on a 401, none of which
 * this page wants.
 *
 * API Gateway cuts every request off at 29 seconds, so after 30 no answer can still be coming and
 * the request is abandoned with a "try again" message. A network failure gets its own message
 * instead of the browser's ("Load failed" in Safari). Server errors carry the server's message.
 */
import {
  builtEnvironment,
  isRunningLocal,
  type AdvisorBrandingMode,
  type ReturningIntakeChoice,
} from '@shared';
import { timeoutSignal } from '@client/util/timeoutSignal';

const CLIENT_INVITE_TIMEOUT_MS = 30_000;
const CLIENT_INVITE_TIMEOUT_MESSAGE =
  'This is taking longer than expected. Please check your connection and try again.';
const CLIENT_INVITE_NETWORK_MESSAGE =
  'We could not connect to Waterlily. Please check your connection and try again.';

export interface AdvisorPublicProfile {
  advisorId: string;
  advisorFirstName: string;
  advisorLastName: string;
  organizationDisplayName: string | null;
  organizationName: string | null;
  advisorLogoKey: string | null;
  whitelabelBrandingPreference: WhitelabelBrandingPreference;
  brandingMode: AdvisorBrandingMode;
  consumerSmsMfaRequired: boolean;
  /** Absent from an older server; the reader treats that as withheld. */
  supportChatWithheld?: boolean;
}

/** `status` is the HTTP status, or 0 when no answer could be read at all. */
export class AdvisorPublicProfileError extends Error {
  constructor(readonly status: number) {
    super(`Advisor public profile request failed (${status || 'no response'})`);
  }
}

export interface ClientInviteRequest {
  advisorId: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  hasAccessToResults?: boolean;
  clientRelationshipStage?: string;
  customReferralSourceId?: string;
  consumerSmsMfa?: { phoneNumber: string };
  returningChoice?: ReturningIntakeChoice;
  inviteRequestId?: string;
}

export interface ClientInviteResponse {
  redirectUrl?: string;
  requiresSignIn?: boolean;
  requiresChoice?: boolean;
}

async function getAdvisorPublicProfile(advisorId: string): Promise<AdvisorPublicProfile> {
  const env = isRunningLocal() ? 'dev' : builtEnvironment;
  let response: Response;
  try {
    response = await fetch(
      `/${env}/api/public/advisor-public-profile/${encodeURIComponent(advisorId)}`,
    );
  } catch {
    throw new AdvisorPublicProfileError(0);
  }
  if (!response.ok) {
    throw new AdvisorPublicProfileError(response.status);
  }
  try {
    return (await response.json()) as AdvisorPublicProfile;
  } catch {
    throw new AdvisorPublicProfileError(0);
  }
}

async function postClientInvite(body: ClientInviteRequest): Promise<ClientInviteResponse> {
  const env = isRunningLocal() ? 'dev' : builtEnvironment;
  const signal = timeoutSignal(CLIENT_INVITE_TIMEOUT_MS);

  try {
    const response = await fetch(`/${env}/api/public/client-invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.errorMessage || errorData.message || 'Unable to process your request',
      );
    }

    const data = (await response.json()) as ClientInviteResponse;
    if (!data.requiresChoice && !data.redirectUrl) {
      throw new Error('No redirect URL received');
    }
    return data;
  } catch (error) {
    if (signal.aborted) {
      throw new Error(CLIENT_INVITE_TIMEOUT_MESSAGE);
    }
    if (error instanceof TypeError) {
      throw new Error(CLIENT_INVITE_NETWORK_MESSAGE);
    }
    throw error;
  }
}

export const publicInviteApi = {
  getAdvisorPublicProfile,
  postClientInvite,
};
